/**
 * Printers plugged straight into this device's browser (spec §6.2):
 *   WebUSB        Chrome/Edge on desktop and Android. On Windows the installed printer driver
 *                 usually holds the device, so prefer the print agent there.
 *   Web Serial    Chrome/Edge desktop (COM / USB-serial printers).
 *   Web Bluetooth Chrome on Android/desktop, Bluetooth LE printers only — classic Bluetooth
 *                 (SPP) printers are invisible to browsers.
 * Safari / iOS support none of these: use the print agent (or the browser print dialog).
 */
export const DEVICE_SUPPORT = Object.freeze({
  webusb: typeof navigator !== 'undefined' && 'usb' in navigator,
  webserial: typeof navigator !== 'undefined' && 'serial' in navigator,
  webbluetooth: typeof navigator !== 'undefined' && 'bluetooth' in navigator,
});

// GATT services used by common BLE receipt printers (vendor "transparent UART" profiles).
const BLE_SERVICES = [
  '000018f0-0000-1000-8000-00805f9b34fb',
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
  '49535343-fe7d-4ae5-8fa9-9fafd205e455',
  '0000ff00-0000-1000-8000-00805f9b34fb',
  '0000fff0-0000-1000-8000-00805f9b34fb',
  '0000ae30-0000-1000-8000-00805f9b34fb',
];

export class DevicePrinterError extends Error {
  constructor(message, { reconnect = false } = {}) {
    super(message);
    this.reconnect = reconnect; // needs a tap on "Reconnect" (browser permission / user gesture)
  }
}

const open = new Map(); // printerId → live handle
const lastStatus = new Map(); // printerId → { state, message }

/** Ask the browser for a printer (must run inside a click). Returns what to store on the printer. */
export async function pickDevicePrinter(api, { baudRate = 9600 } = {}) {
  if (api === 'webusb') {
    const d = await navigator.usb.requestDevice({ filters: [] });
    return {
      usb: {
        vendorId: d.vendorId,
        productId: d.productId,
        serialNumber: d.serialNumber || null,
        productName: d.productName || null,
      },
      label: d.productName || 'USB printer',
    };
  }
  if (api === 'webserial') {
    const port = await navigator.serial.requestPort();
    const info = port.getInfo();
    return {
      serial: { usbVendorId: info.usbVendorId ?? null, usbProductId: info.usbProductId ?? null, baudRate },
      label: 'Serial printer',
    };
  }
  if (api === 'webbluetooth') {
    const d = await navigator.bluetooth.requestDevice({ acceptAllDevices: true, optionalServices: BLE_SERVICES });
    open.delete(`bt:${d.id}`);
    rememberBluetooth(d);
    return { bluetooth: { id: d.id, name: d.name || null }, label: d.name || 'Bluetooth printer' };
  }
  throw new Error('Unknown connection');
}

// ---------------------------------------------------------------- USB
async function usbHandle(p) {
  const want = p.connection.usb ?? {};
  let h = open.get(p.id);
  if (h?.device?.opened) return h;
  const devices = await navigator.usb.getDevices();
  const device = devices.find(
    (d) =>
      d.vendorId === want.vendorId &&
      d.productId === want.productId &&
      (!want.serialNumber || d.serialNumber === want.serialNumber),
  );
  if (!device)
    throw new DevicePrinterError('USB printer not found. Plug it in, then tap Reconnect.', { reconnect: true });
  if (!device.opened) await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);
  let target = null;
  for (const iface of device.configuration.interfaces) {
    for (const alt of iface.alternates) {
      const ep = alt.endpoints.find((e) => e.direction === 'out' && e.type === 'bulk');
      if (ep && (!target || alt.interfaceClass === 7)) target = { iface: iface.interfaceNumber, ep: ep.endpointNumber };
    }
  }
  if (!target) throw new DevicePrinterError('This USB device has no printer interface.');
  try {
    await device.claimInterface(target.iface);
  } catch {
    throw new DevicePrinterError(
      'The USB printer is in use by its Windows driver. Use the print agent on this PC instead of WebUSB.',
    );
  }
  h = { device, ...target };
  open.set(p.id, h);
  return h;
}

async function sendUsb(p, bytes) {
  const h = await usbHandle(p);
  for (let i = 0; i < bytes.length; i += 16_384) {
    const r = await h.device.transferOut(h.ep, bytes.subarray(i, i + 16_384));
    if (r.status !== 'ok') throw new DevicePrinterError(`USB transfer ${r.status}`);
  }
}

// ---------------------------------------------------------------- Serial
async function sendSerial(p, bytes) {
  const want = p.connection.serial ?? {};
  let port = open.get(p.id)?.port;
  if (!port) {
    const ports = await navigator.serial.getPorts();
    port =
      ports.find((x) => {
        const i = x.getInfo();
        return want.usbVendorId != null && i.usbVendorId === want.usbVendorId && i.usbProductId === want.usbProductId;
      }) ?? (ports.length === 1 ? ports[0] : null);
    if (!port)
      throw new DevicePrinterError('Serial printer not found. Tap Reconnect and choose its port.', { reconnect: true });
    if (!port.writable) await port.open({ baudRate: want.baudRate ?? 9600 });
    open.set(p.id, { port });
  }
  const writer = port.writable.getWriter();
  try {
    await writer.write(bytes);
  } finally {
    writer.releaseLock();
  }
}

// ---------------------------------------------------------------- Bluetooth LE
const btDevices = new Map(); // device id → BluetoothDevice granted in this session

function rememberBluetooth(d) {
  btDevices.set(d.id, d);
}

async function bleCharacteristic(device) {
  const server = device.gatt.connected ? device.gatt : await device.gatt.connect();
  for (const uuid of BLE_SERVICES) {
    let service;
    try {
      service = await server.getPrimaryService(uuid);
    } catch {
      continue;
    }
    const chars = await service.getCharacteristics();
    const c = chars.find((x) => x.properties.writeWithoutResponse || x.properties.write);
    if (c) return c;
  }
  throw new DevicePrinterError('This Bluetooth device does not look like a printer.');
}

async function sendBluetooth(p, bytes) {
  const want = p.connection.bluetooth ?? {};
  let h = open.get(p.id);
  if (!h?.device?.gatt?.connected) {
    let device = btDevices.get(want.id);
    if (!device && navigator.bluetooth.getDevices)
      device = (await navigator.bluetooth.getDevices()).find((d) => d.id === want.id);
    if (!device) throw new DevicePrinterError('Tap Reconnect to connect the Bluetooth printer.', { reconnect: true });
    rememberBluetooth(device);
    h = { device, char: await bleCharacteristic(device) };
    open.set(p.id, h);
  }
  const fast = h.char.properties.writeWithoutResponse;
  // Small chunks with a short pause: cheap BLE printers have tiny buffers.
  for (let i = 0; i < bytes.length; i += 100) {
    const chunk = bytes.subarray(i, i + 100);
    if (fast) {
      await h.char.writeValueWithoutResponse(chunk);
      await new Promise((r) => setTimeout(r, 15));
    } else await h.char.writeValueWithResponse(chunk);
  }
}

/** Re-grant a Bluetooth/USB/serial printer for this session (call from a click). */
export async function reconnectDevicePrinter(p) {
  open.delete(p.id);
  const c = p.connection;
  if (c.api === 'webbluetooth') {
    const d = await navigator.bluetooth.requestDevice({
      ...(c.bluetooth?.name ? { filters: [{ name: c.bluetooth.name }] } : { acceptAllDevices: true }),
      optionalServices: BLE_SERVICES,
    });
    rememberBluetooth(d);
    p.connection.bluetooth = { ...(c.bluetooth ?? {}), id: d.id };
    return { bluetooth: { id: d.id, name: d.name || c.bluetooth?.name || null } };
  }
  if (c.api === 'webusb') {
    await navigator.usb.requestDevice({
      filters: c.usb ? [{ vendorId: c.usb.vendorId, productId: c.usb.productId }] : [],
    });
    return null;
  }
  if (c.api === 'webserial') {
    await navigator.serial.requestPort();
    return null;
  }
  return null;
}

export async function sendToDevicePrinter(p, bytes) {
  const api = p.connection.api;
  if (!DEVICE_SUPPORT[api])
    throw new DevicePrinterError('This browser cannot reach that printer. Use Chrome or Edge, or the print agent.');
  try {
    if (api === 'webusb') await sendUsb(p, bytes);
    else if (api === 'webserial') await sendSerial(p, bytes);
    else await sendBluetooth(p, bytes);
    lastStatus.set(p.id, { state: 'online', message: 'Ready' });
  } catch (err) {
    open.delete(p.id);
    lastStatus.set(p.id, { state: 'error', message: err.message.slice(0, 120) });
    throw err instanceof DevicePrinterError ? err : new DevicePrinterError(err.message);
  }
}

/** Reported with the device heartbeat so managers see these printers' health. */
export function devicePrinterStatuses() {
  return Object.fromEntries(lastStatus);
}
